import { moment } from '@international/mastodon-i18n';
import { ReportVerification } from 'api/candidates/types/Report';

export const VERIFICATION_STATES = {
  CANCELED: 'canceled',
  COMPLETE: 'complete',
  EXPIRED: 'expired',
  REQUIRED: 'required',
} as const;

export type VerificationState =
  typeof VERIFICATION_STATES[keyof typeof VERIFICATION_STATES];

const KEY_PREFIX = 'components.Verifications.Verification';

// Precedence follows the detail page (VerificationBase/DisplayComponents):
// canceled, then complete, then expired, then required. Unlike the detail
// page, the list endpoint does not send `completionAllowed` today (see
// EVERI-5091), so when it is absent fall back to comparing `expiresAt` with
// the device clock -- the backend derives `completionAllowed` the same way.
const getVerificationState = (
  verification: ReportVerification,
): VerificationState => {
  if (verification.canceledAt) return VERIFICATION_STATES.CANCELED;
  if (verification.completedAt) return VERIFICATION_STATES.COMPLETE;
  if (verification.completionAllowed === false)
    return VERIFICATION_STATES.EXPIRED;
  if (verification.completionAllowed === undefined && verification.expiresAt) {
    const expiresAt = moment(verification.expiresAt);
    if (expiresAt.isValid() && expiresAt.isBefore(moment()))
      return VERIFICATION_STATES.EXPIRED;
  }
  return VERIFICATION_STATES.REQUIRED;
};

const TEXT_KEYS: Record<VerificationState, string> = {
  canceled: 'canceledText',
  complete: 'completedText',
  expired: 'expiredText',
  required: 'text',
};

const TAG_KEYS: Record<VerificationState, string> = {
  canceled: `${KEY_PREFIX}.tags.canceled`,
  complete: `${KEY_PREFIX}.tags.reviewing`,
  expired: `${KEY_PREFIX}.tags.expired`,
  required: `${KEY_PREFIX}.tags.required`,
};

const ICONS: Record<VerificationState, string> = {
  canceled: 'InformationFilled',
  complete: 'Renew',
  expired: 'InformationFilled',
  required: 'WarningFilled',
};

// Only complete and required rows carry a date line; canceled and expired
// rows must not show "Complete by".
const DATE_FIELDS: Partial<
  Record<VerificationState, 'completedAt' | 'expiresAt'>
> = {
  complete: 'completedAt',
  required: 'expiresAt',
};

const DATE_KEYS: Partial<Record<VerificationState, string>> = {
  complete: `${KEY_PREFIX}.date.completedAt`,
  required: `${KEY_PREFIX}.date.expireAt`,
};

const useVerificationData = (
  verification: ReportVerification,
  verificationName: string,
) => {
  const state = getVerificationState(verification);
  const isActionable = state === VERIFICATION_STATES.REQUIRED;
  const dateField = DATE_FIELDS[state];
  const dateValue = dateField ? verification[dateField] : null;
  const date = dateValue ? moment(dateValue) : null;

  return {
    state,
    link: `/verifications/${verification.id}`,
    className: isActionable ? 'warning' : 'caution',
    iconName: ICONS[state],
    isActionable,
    date: date?.isValid() ? date : null,
    translationsKeys: {
      title: [
        `${KEY_PREFIX}.${verificationName}.title`,
        `${KEY_PREFIX}.Default.title`,
      ],
      text: [
        `${KEY_PREFIX}.${verificationName}.${TEXT_KEYS[state]}`,
        `${KEY_PREFIX}.Default.${TEXT_KEYS[state]}`,
      ],
      tag: TAG_KEYS[state],
      date: DATE_KEYS[state] ?? null,
    },
  };
};

export default useVerificationData;
